import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut, LayoutDashboard, ShoppingBag, ShoppingBasket, ClipboardList, ScanLine, Layers,
  PackageOpen, Truck, Users, Star, Boxes as BoxesIcon, Store, Sparkles, Wallet, Settings, Inbox,
  FolderTree, Ticket, Ship, Anchor, Image as ImageIcon, UserCog, Activity, AlertTriangle, Zap,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import DashboardHome from "./admin/DashboardHome";
import Catalog from "./admin/Catalog";
import OrdersAdmin from "./admin/OrdersAdmin";
import BoxesAdmin from "./admin/BoxesAdmin";
import Stores from "./admin/Stores";
import Categories from "./admin/Categories";
import RequestsAdmin from "./admin/RequestsAdmin";
import ReviewsAdmin from "./admin/ReviewsAdmin";
import Finance from "./admin/Finance";
import AIAssistant from "./admin/AIAssistant";
import Customers from "./admin/Customers";
import SettingsAdmin from "./admin/SettingsAdmin";
import Promotions from "./admin/Promotions";
import Banners from "./admin/Banners";
import FxOrders from "./admin/FxOrders";
import FxPicking from "./admin/FxPicking";
import FxWaves from "./admin/FxWaves";
import FxPacking from "./admin/FxPacking";
import FxContainers from "./admin/FxContainers";
import FxInbound from "./admin/FxInbound";
import FxDelivery from "./admin/FxDelivery";
import FxStaff from "./admin/FxStaff";
import StaffHome from "./admin/StaffHome";
import OpsBoard from "./admin/OpsBoard";
import Shipments from "./admin/Shipments";
import FxExceptions from "./admin/FxExceptions";
import FxShopping from "./admin/FxShopping";
import FxPrepare from "./admin/FxPrepare";
import logoLight from "../assets/afrigate-logo-light.png";

export const STAFF_ROLES = ["admin", "ops", "picker", "packer", "loader", "inbound", "delivery", "support"];

// fulfilment module -> tab id + metadata
const FX_META = {
  "fx-orders": { label: "Orders", icon: ClipboardList },
  "fx-shopping": { label: "Shopping List", icon: ShoppingBasket },
  "fx-prepare": { label: "Prepare", icon: PackageOpen },
  "fx-picking": { label: "Picking", icon: ScanLine },
  "fx-waves": { label: "Pick Waves", icon: Layers },
  "fx-packing": { label: "Packing", icon: PackageOpen },
  "fx-containers": { label: "Containers", icon: Ship },
  "fx-inbound": { label: "Ghana Inbound", icon: Anchor },
  "fx-delivery": { label: "Delivery", icon: Truck },
  "fx-exceptions": { label: "Exceptions", icon: AlertTriangle },
};
const MODULE_TAB = { orders: "fx-orders", shopping: "fx-shopping", prepare: "fx-prepare", picking: "fx-picking", waves: "fx-waves", packing: "fx-packing", containers: "fx-containers", inbound: "fx-inbound", delivery: "fx-delivery", exceptions: "fx-exceptions" };
// which fx tabs each staff role sees
const ROLE_MODULES = {
  ops: ["prepare", "containers", "inbound", "exceptions", "orders"],
  picker: ["shopping", "picking", "waves", "orders", "exceptions"], packer: ["packing", "orders"], loader: ["containers", "orders"],
  inbound: ["inbound", "orders"], delivery: ["delivery", "orders"], support: ["orders", "exceptions"],
};
const ROLE_TITLE = { ops: "Operations", picker: "Picker Workspace", packer: "Packing Station", loader: "Consolidation", inbound: "Ghana Inbound", delivery: "Last-Mile Delivery", support: "Customer Support" };

// Lite mode: collapse the Fulfilment OS group to a lean set (Ops / Delivery / Admin style)
const LITE_FX = new Set(["fx-board", "fx-orders", "fx-prepare", "fx-containers", "fx-inbound", "shipments", "fx-delivery", "fx-exceptions", "payments"]);

const ADMIN_NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { group: "Fulfilment OS", items: [
    { id: "fx-board", label: "Ops Board", icon: Activity },
    { id: "fx-orders", label: "Orders", icon: ClipboardList },
    { id: "fx-shopping", label: "Shopping List", icon: ShoppingBasket },
    { id: "fx-prepare", label: "Prepare (pick + pack)", icon: PackageOpen },
    { id: "fx-picking", label: "Picking", icon: ScanLine },
    { id: "fx-waves", label: "Pick Waves", icon: Layers },
    { id: "fx-packing", label: "Packing", icon: PackageOpen },
    { id: "fx-containers", label: "Containers", icon: Ship },
    { id: "fx-inbound", label: "Ghana Inbound", icon: Anchor },
    { id: "shipments", label: "Shipments", icon: Ship },
    { id: "fx-delivery", label: "Delivery", icon: Truck },
    { id: "fx-exceptions", label: "Exceptions", icon: AlertTriangle },
    { id: "payments", label: "Payments", icon: Wallet },
  ] },
  { group: "Catalog", items: [
    { id: "catalog", label: "Catalog Manager", icon: ShoppingBag },
    { id: "boxes", label: "Boxes", icon: BoxesIcon },
    { id: "stores", label: "Stores & Suppliers", icon: Store },
    { id: "categories", label: "Categories", icon: FolderTree },
    { id: "promos", label: "Promotions", icon: Ticket },
    { id: "banners", label: "Banners", icon: ImageIcon },
  ] },
  { group: "Customers", items: [
    { id: "customers", label: "Customers", icon: Users },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "requests", label: "Requests", icon: Inbox },
  ] },
  { group: "Finance & Insights", items: [
    { id: "finance", label: "Finance", icon: Wallet },
    { id: "ai", label: "AI Assistant", icon: Sparkles },
  ] },
  { group: "Team", items: [
    { id: "staff", label: "Staff & Roles", icon: UserCog },
  ] },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [fx, setFx] = useState(19.5);
  const [badges, setBadges] = useState({ requests: 0, drafts: 0 });
  const [lite, setLite] = useState(() => localStorage.getItem("fx_lite") === "1");

  const role = user && user.role;
  const isStaff = user && STAFF_ROLES.includes(role);
  const isAdmin = role === "admin";

  // build nav for the current role
  const adminNav = lite
    ? ADMIN_NAV.map((n) => (n.group === "Fulfilment OS" ? { ...n, items: n.items.filter((i) => LITE_FX.has(i.id)) } : n))
    : ADMIN_NAV;
  const nav = isAdmin ? adminNav
    : [{ group: ROLE_TITLE[role] || "Workspace", items: [{ id: "fx-home", label: "Home", icon: LayoutDashboard }, ...(ROLE_MODULES[role] || []).map((m) => ({ id: MODULE_TAB[m], ...FX_META[MODULE_TAB[m]] }))] }];
  const flatNav = nav.flatMap((n) => (n.group ? n.items : [n]));
  const defaultTab = isAdmin ? "dashboard" : "fx-home";
  const [tab, setTab] = useState(params.get("tab") || defaultTab);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/dashboard").then((r) => setFx(r.data.fx_gbp_ghs || 19.5)).catch(() => navigate("/admin/login"));
      api.get("/admin/stats").then((r) => setBadges({ requests: r.data.open_requests || 0, drafts: r.data.draft_products || 0 })).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user, tab]);

  if (!user || !isStaff) return <div className="min-h-screen flex items-center justify-center text-[#5C5C5C]">Loading…</div>;
  // guard: hide-away tabs (staff scope, or admin Lite mode) fall back to default
  if (!flatNav.find((n) => n.id === tab)) { setTab(defaultTab); }

  const label = flatNav.find((n) => n.id === tab)?.label || "Dashboard";
  const badgeFor = (id) => (id === "requests" ? badges.requests : id === "catalog" ? badges.drafts : 0);

  const render = () => {
    switch (tab) {
      case "dashboard": return <DashboardHome onGo={setTab} />;
      case "fx-board": return <OpsBoard onGo={setTab} />;
      case "fx-home": return <StaffHome role={role} onGo={setTab} />;
      case "fx-orders": return <FxOrders role={role} />;
      case "fx-shopping": return <FxShopping />;
      case "fx-prepare": return <FxPrepare />;
      case "fx-picking": return <FxPicking />;
      case "fx-waves": return <FxWaves />;
      case "fx-packing": return <FxPacking />;
      case "fx-containers": return <FxContainers />;
      case "fx-inbound": return <FxInbound />;
      case "shipments": return <Shipments />;
      case "fx-delivery": return <FxDelivery />;
      case "fx-exceptions": return <FxExceptions />;
      case "payments": return <OrdersAdmin />;
      case "staff": return <FxStaff />;
      case "catalog": return <Catalog fx={fx} />;
      case "boxes": return <BoxesAdmin fx={fx} />;
      case "stores": return <Stores />;
      case "categories": return <Categories />;
      case "promos": return <Promotions fx={fx} />;
      case "banners": return <Banners />;
      case "requests": return <RequestsAdmin />;
      case "reviews": return <ReviewsAdmin />;
      case "finance": return <Finance />;
      case "ai": return <AIAssistant />;
      case "customers": return <Customers />;
      case "settings": return <SettingsAdmin />;
      default: return null;
    }
  };

  const navBtn = (n) => (
    <button key={n.id} onClick={() => setTab(n.id)} data-testid={`nav-${n.id}`}
      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium ${tab === n.id ? "bg-[#E7E1B1] text-[#0D530E]" : "text-[#FBF5DD]/80 hover:bg-white/10"}`}
      style={{ transition: "background-color 0.2s ease" }}>
      <n.icon size={17} /> {n.label}
      {badgeFor(n.id) > 0 && <span className="ml-auto text-[10px] font-bold bg-[#D9933B] text-white rounded-full px-2 py-0.5">{badgeFor(n.id)}</span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FBF5DD] flex">
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-[#0D530E] text-[#FBF5DD] sticky top-0 h-screen">
        <div className="flex flex-col gap-2.5 px-5 py-5 border-b border-white/10 shrink-0">
          <img src={logoLight} alt="AfriGate" className="h-16 w-auto" />
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0D530E] bg-[#E7E1B1] rounded-full px-2.5 py-1 w-fit">
            <ShoppingBasket size={13} /> {isAdmin ? "Ops & Store Admin" : (ROLE_TITLE[role] || "Staff")}
          </span>
        </div>
        {isAdmin && (
          <button onClick={() => { const v = !lite; setLite(v); localStorage.setItem("fx_lite", v ? "1" : "0"); }} data-testid="lite-mode-toggle"
            className="mx-3 mt-3 flex items-center justify-between gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-white/10 hover:bg-white/15 text-[#FBF5DD]"
            title="Lite mode collapses the back-office to the essentials (Ops · Delivery · Admin)">
            <span className="flex items-center gap-2"><Zap size={14} /> Lite mode {lite ? "on" : "off"}</span>
            <span className={`w-9 h-5 rounded-full p-0.5 flex items-center ${lite ? "bg-[#E7E1B1] justify-end" : "bg-white/20 justify-start"}`} style={{ transition: "background-color 0.2s ease" }}>
              <span className="w-4 h-4 rounded-full bg-white block" />
            </span>
          </button>
        )}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto no-scrollbar">
          {nav.map((n) => n.group ? (
            <div key={n.group} className="pt-3">
              <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#E7E1B1]/60">{n.group}</p>
              <div className="space-y-0.5">{n.items.map(navBtn)}</div>
            </div>
          ) : navBtn(n))}
        </nav>
        <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
          className="m-3 shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-[#FBF5DD]/80 hover:bg-white/10">
          <LogOut size={16} /> Sign out
        </button>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden flex gap-2 overflow-x-auto no-scrollbar p-3 bg-[#0D530E] sticky top-0 z-30">
          {flatNav.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${tab === n.id ? "bg-[#E7E1B1] text-[#0D530E]" : "bg-white/10 text-[#FBF5DD]"}`}>{n.label}</button>
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          <h1 className="font-display text-3xl sm:text-4xl text-[#0D530E] mb-6">{label}</h1>
          {render()}
        </div>
      </main>
    </div>
  );
}
